// React Hooks:
import React, { useState, useEffect } from "react";
// React-Router-Dom:
import { useNavigate } from "react-router-dom";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import { shareManagement } from "../shareManagementSlice";
import {
  linkFileModalAction,
  setLinkFileModalAction,
} from "../../../global/link-file-modal/linkFileModalSlice";
import {
  deleteModalOpenAction,
  deleteModalSetActionPathAction,
  deleteModalSetDeleteIdAction,
} from "../../../global/delete-modal/deleteModalSlice";
import {
  setSubReportsUniqIdAction,
  subReportsModalOpenAction,
} from "../../../global/sub-reports-modal/subReportsModalSlice";
// MUI Components:
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  SvgIcon,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";
// Components:
import EditNumber from "./edit-number/EditNumber";
// MUI Styles:
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { tableCellClasses } from "@mui/material/TableCell";
// Icones:
import { ReactComponent as SquareCheckIcon } from "../../../../assets/icons/squareCheck-icon.svg";
import { ReactComponent as SquareRemoveIcon } from "../../../../assets/icons/squareRemove-icon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right-icon.svg";
import { ReactComponent as TableIcon } from "../../../../assets/icons/table-icon.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/icons/copy-icon.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/icons/trash2-icon.svg";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: "14px",
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.text.primary,
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    maxWidth: 100,
    fontSize: "14px",
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    color: "inherit",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.background.paper,
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.background.paper,
    filter: "brightness(95%)",
  },
  color: "#A1A2A3",
  transition: "background 0.3s",
  "&:hover": {
    backgroundColor: "#e1e1e1",
    color: "inherit",
  },
}));

const useStyles = makeStyles((theme) => ({
  tableBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  tableRoot: {
    "& th:last-child": {
      borderTopRightRadius: "8px",
    },
    "& tr:last-child": {
      borderRadius: "8px",
    },
    "& th:first-child": {
      borderTopLeftRadius: "8px",
    },
  },
}));

const ShareManagementTable = () => {
  const classes = useStyles();
  const theme = useTheme();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    shareManagementData: { list },
  } = useAppSelector(shareManagement);

  const [sort, setSort] = useState({
    name: "right",
    password: "right",
    alternative: "right",
  });

  useEffect(() => {
    if (!list?.length) {
      navigate("/");
    }
  }, [list?.length, navigate]);

  const sortHandler = (name: string) => {
    switch (name) {
      case "name":
        setSort((last) => ({
          ...last,
          name:
            last.name === "right"
              ? "down"
              : last.name === "down"
                ? "up"
                : last.name === "up"
                  ? "right"
                  : "",
          password: "right",
          alternative: "right",
        }));
        break;
      case "password":
        setSort((last) => ({
          ...last,
          password:
            last.password === "right"
              ? "down"
              : last.password === "down"
                ? "up"
                : last.password === "up"
                  ? "right"
                  : "",
          name: "right",
          alternative: "right",
        }));
        break;
      case "alternative":
        setSort((last) => ({
          ...last,
          alternative:
            last.alternative === "right"
              ? "down"
              : last.alternative === "down"
                ? "up"
                : last.alternative === "up"
                  ? "right"
                  : "",
          name: "right",
          password: "right",
        }));
        break;
      default:
        break;
    }
  };

  const sortSvgIcon = (sort: string) => {
    switch (sort) {
      case "down":
        return "rotate(90deg)";
      case "up":
        return "rotate(-90deg)";
      case "right":
        return "rotate(0deg)";
      default:
        return;
    }
  };

  const sortFunction = (
    items: any,
    variableItem: string,
    direction: string
  ) => {
    let firstSort;
    let secondSort;

    switch (direction) {
      case "a-z":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? -1 : x > y ? 1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`]?.toLowerCase();
          const y = b[`${variableItem}`]?.toLowerCase();

          return x < y ? -1 : x > y ? 1 : 0;
        });

        return secondSort;

      case "z-a":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? 1 : x > y ? -1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`]?.toLowerCase();
          const y = b[`${variableItem}`]?.toLowerCase();

          return x < y ? 1 : x > y ? -1 : 0;
        });

        return secondSort;

      default:
        return items;
    }
  };

  const sortItems = (items: any) => {
    if (sort.name === "down") {
      return sortFunction(items, "name", "a-z");
    }

    if (sort.name === "up") {
      return sortFunction(items, "name", "z-a");
    }

    if (sort.password === "down") {
      const truePassword = [...items].filter(
        (item: any) => item.password === true
      );
      const falsePassword = [...items].filter(
        (item: any) => item.password === false
      );

      return [...truePassword, ...falsePassword];
    }

    if (sort.password === "up") {
      const truePassword = [...items].filter(
        (item: any) => item.password === true
      );
      const falsePassword = [...items].filter(
        (item: any) => item.password === false
      );

      return [...falsePassword, ...truePassword];
    }

    if (sort.alternative === "down") {
      return sortFunction(items, "alternative", "a-z");
    }

    if (sort.alternative === "up") {
      return sortFunction(items, "alternative", "z-a");
    }

    return items;
  };

  return (
    <TableContainer
      component={Box}
      sx={{
        minHeight: "130px",
        maxHeight: "100%",
        borderRadius: "8px",
      }}
      className={classes.tableBox}
    >
      <Table stickyHeader className={classes.tableRoot}>
        <TableHead>
          <TableRow>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
            <StyledTableCell align="left" width="15%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.name),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("name")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Name
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="25%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                Description
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left" width="10%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.password),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("password")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  password
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="15%">
              <Typography
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                Number
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left" width="17%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.alternative),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("alternative")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Alternative
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
            <StyledTableCell align="left" width="6%"></StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortItems(list)?.map((row: any, index: number) => (
            <StyledTableRow key={index}>
              <StyledTableCell align="left">{index + 1}</StyledTableCell>
              <StyledTableCell align="left">
                <Tooltip
                  arrow
                  placement="bottom"
                  title={row.name}
                  enterDelay={500}
                  enterNextDelay={1000}
                  leaveDelay={500}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "10px !important",
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "20px",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      fontSize: "inherit",
                      color: "inherit",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  >
                    {row.name}
                  </Typography>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Tooltip
                  arrow
                  placement="bottom"
                  title={row.description}
                  enterDelay={100}
                  enterNextDelay={100}
                  leaveDelay={100}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "10px !important",
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      fontSize: "inherit",
                      color: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                      cursor: "pointer"
                    }}
                  >
                    {row?.description}
                  </Typography>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SvgIcon
                    component={
                      row.password ? SquareCheckIcon : SquareRemoveIcon
                    }
                    inheritViewBox
                    sx={{
                      width: "24px",
                      height: "24px",
                      color: row.password ? "#3AEC93" : "#E36464",
                      mr: "8px",
                      [theme.breakpoints.down("lg")]: {
                        width: "22px",
                        height: "22px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "20px",
                        height: "20px",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "inherit",
                      color: "inherit",
                      lineHeight: "1.8",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    {row.password ? "Ok" : "No"}
                  </Typography>
                </Box>
              </StyledTableCell>
              <StyledTableCell align="left">
                <EditNumber item={row} />
              </StyledTableCell>
              <StyledTableCell align="left">
                <Typography
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    height: "20px",
                    overflow: "hidden",
                    fontSize: "inherit",
                    color: "inherit",
                    [theme.breakpoints.down("md")]: {
                      height: "14px",
                    },
                  }}
                >
                  {row.alternative}
                </Typography>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Tooltip
                  arrow
                  placement="bottom"
                  title="Reports"
                  enterDelay={500}
                  enterNextDelay={1000}
                  leaveDelay={500}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "10px !important",
                      },
                    },
                  }}
                >
                  <SvgIcon
                    component={TableIcon}
                    inheritViewBox
                    sx={{
                      width: "auto",
                      height: "18px",
                      color: "#A1A2A3",
                      cursor: "pointer",
                      [theme.breakpoints.down("lg")]: {
                        height: "16px",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(subReportsModalOpenAction());
                      dispatch(setSubReportsUniqIdAction(row.uniq_id));
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Tooltip
                  arrow
                  placement="bottom"
                  title="Link File"
                  enterDelay={500}
                  enterNextDelay={1000}
                  leaveDelay={500}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "10px !important",
                      },
                    },
                  }}
                >
                  <SvgIcon
                    component={CopyIcon}
                    inheritViewBox
                    sx={{
                      width: "auto",
                      height: "18px",
                      color: "#A1A2A3",
                      cursor: "pointer",
                      [theme.breakpoints.down("lg")]: {
                        height: "16px",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(linkFileModalAction(true));
                      dispatch(setLinkFileModalAction(row.link));
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Tooltip
                  arrow
                  placement="bottom"
                  title="Delete File"
                  enterDelay={500}
                  enterNextDelay={1000}
                  leaveDelay={500}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        marginTop: "10px !important",
                      },
                    },
                  }}
                >
                  <SvgIcon
                    component={TrashIcon}
                    inheritViewBox
                    sx={{
                      width: "auto",
                      height: "16px",
                      color: "#E36464",
                      cursor: "pointer",
                      [theme.breakpoints.down("lg")]: {
                        height: "14px",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(deleteModalOpenAction({ type: "Link" }));
                      dispatch(
                        deleteModalSetActionPathAction("share-management")
                      );
                      dispatch(deleteModalSetDeleteIdAction(row.uniq_id));
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ShareManagementTable;
